import { PostgrestResponse } from "@supabase/supabase-js";
import { IConfiguracoes } from "../../../types/tables/IConfiguracoes";
import { BaseMultiTenantService } from "../base/BaseMultiTenantService";

/**
 * Serviço para gerenciamento de configurações com isolamento multi-tenant
 * Gerencia configurações específicas de cada organização
 */
export class ConfiguracoesService extends BaseMultiTenantService<IConfiguracoes> {
  constructor() {
    super("configuracoes", ["chave", "descricao"], "fk_id_organizacao");
  }

  /**
   * Busca configuração por chave
   */
  async getByChave(
    organizationId: number,
    chave: string,
  ): Promise<PostgrestResponse<IConfiguracoes>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq(this.organizationIdField, organizationId)
      .eq("chave", chave)
      .single();
  }

  /**
   * Define valor de uma configuração (cria se não existir)
   */
  async setConfiguracao(
    organizationId: number,
    chave: string,
    valor: string,
    descricao?: string,
    userId?: number,
  ): Promise<PostgrestResponse<IConfiguracoes[]>> {
    const { data: existenteRaw } = await this.getByChave(organizationId, chave);
    const existente = existenteRaw as any; // Conversão temporária

    if (existente) {
      // Atualizar existente
      return await this.update(existente.id, { valor }, organizationId, userId);
    } else {
      // Criar nova
      return await this.create(
        {
          chave,
          valor,
          descricao: descricao || `Configuração ${chave}`,
        },
        organizationId,
        userId,
      );
    }
  }

  /**
   * Busca valor de uma configuração (retorna string)
   */
  async getValor(
    organizationId: number,
    chave: string,
    valorPadrao?: string,
  ): Promise<string | null> {
    const { data: dataRaw } = await this.getByChave(organizationId, chave);
    const data = dataRaw as any; // Conversão temporária
    return data?.valor || valorPadrao || null;
  }

  /**
   * Busca valor booleano de uma configuração
   */
  async getValorBoolean(
    organizationId: number,
    chave: string,
    valorPadrao: boolean = false,
  ): Promise<boolean> {
    const valor = await this.getValor(organizationId, chave);
    if (valor === null) return valorPadrao;
    return valor.toLowerCase() === "true" || valor === "1";
  }

  /**
   * Busca valor numérico de uma configuração
   */
  async getValorNumber(
    organizationId: number,
    chave: string,
    valorPadrao: number = 0,
  ): Promise<number> {
    const valor = await this.getValor(organizationId, chave);
    if (valor === null) return valorPadrao;
    const numero = parseFloat(valor);
    return isNaN(numero) ? valorPadrao : numero;
  }

  /**
   * Cria configurações padrão para uma nova organização
   */
  async criarConfiguracoesPadrao(
    organizationId: number,
    userId?: number,
  ): Promise<any[]> {
    const configuracoesPadrao = [
      {
        chave: "limite_diario_padrao",
        valor: "50.00",
        descricao: "Limite diário padrão para alunos (R$)",
      },
      {
        chave: "limite_semanal_padrao",
        valor: "200.00",
        descricao: "Limite semanal padrão para alunos (R$)",
      },
      {
        chave: "permitir_vendas_fim_semana",
        valor: "false",
        descricao: "Permitir vendas aos finais de semana",
      },
      {
        chave: "horario_inicio_vendas",
        valor: "07:00",
        descricao: "Horário de início das vendas",
      },
      {
        chave: "horario_fim_vendas",
        valor: "17:00",
        descricao: "Horário de término das vendas",
      },
      {
        chave: "moeda_padrao",
        valor: "BRL",
        descricao: "Moeda padrão do sistema",
      },
      {
        chave: "timezone",
        valor: "America/Sao_Paulo",
        descricao: "Fuso horário da organização",
      },
      {
        chave: "tema_padrao",
        valor: "light",
        descricao: "Tema padrão da interface (light/dark)",
      },
    ];

    const resultados = [];

    for (const config of configuracoesPadrao) {
      try {
        const resultado = await this.create(config, organizationId, userId);
        resultados.push(resultado);
      } catch (error) {
        console.error(`Erro ao criar configuração ${config.chave}:`, error);
      }
    }

    return resultados;
  }

  /**
   * Exporta todas as configurações da organização
   */
  async exportarConfiguracoes(
    organizationId: number,
  ): Promise<Record<string, string>> {
    const { data: configs } = await this.getAllActive(organizationId);

    if (!configs) return {};

    return configs.reduce(
      (acc: Record<string, string>, config: IConfiguracoes) => {
        acc[config.chave] = config.valor;
        return acc;
      },
      {},
    );
  }

  /**
   * Importa configurações em lote
   */
  async importarConfiguracoes(
    organizationId: number,
    configuracoes: Record<string, string>,
    userId?: number,
  ): Promise<any[]> {
    const resultados = [];

    for (const [chave, valor] of Object.entries(configuracoes)) {
      try {
        const resultado = await this.setConfiguracao(
          organizationId,
          chave,
          valor,
          undefined,
          userId,
        );
        resultados.push(resultado);
      } catch (error) {
        console.error(`Erro ao importar configuração ${chave}:`, error);
      }
    }

    return resultados;
  }

  /**
   * Busca URLs das logos
   */
  async getLogoUrls(organizationId: number): Promise<{
    logo_url?: string;
    logo_light_url?: string;
    logo_dark_url?: string;
  }> {
    const logos = await Promise.all([
      this.getValor(organizationId, "logo_url"),
      this.getValor(organizationId, "logo_light_url"),
      this.getValor(organizationId, "logo_dark_url"),
    ]);

    return {
      logo_url: logos[0] || undefined,
      logo_light_url: logos[1] || undefined,
      logo_dark_url: logos[2] || undefined,
    };
  }

  /**
   * Atualiza URL da logo
   */
  async updateLogoUrl(
    organizationId: number,
    logoType: "logo_url" | "logo_light_url" | "logo_dark_url",
    url: string,
    userId?: number,
  ): Promise<PostgrestResponse<IConfiguracoes[]>> {
    const descricaoMap = {
      logo_url: "URL da logo principal",
      logo_light_url: "URL da logo para tema claro",
      logo_dark_url: "URL da logo para tema escuro",
    };

    return await this.setConfiguracao(
      organizationId,
      logoType,
      url,
      descricaoMap[logoType],
      userId,
    );
  }

  /**
   * Remove configuração de logo
   */
  async removeLogoUrl(
    organizationId: number,
    logoType: "logo_url" | "logo_light_url" | "logo_dark_url",
  ): Promise<boolean> {
    const { data: config } = await this.getByChave(organizationId, logoType);

    if (config) {
      const result = await this.client
        .from(this.tableName)
        .delete()
        .eq("id", (config as any).id)
        .eq(this.organizationIdField, organizationId);

      return !result.error;
    }

    return true; // Se não existe, considera como removido
  }
}

export const configuracoesService = new ConfiguracoesService();
